import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { useApp } from "../context";

// The "active learner" is who the Learn tools serve content for. By default it
// is the signed-in user (learn as myself). A parent/solo account can switch to
// one of their explorers, and every learning tool then adapts to that explorer's
// grade + per-subject levels, exactly as if the explorer had logged in.
const KEY = "isaiah_active_learner";
const EVT = "active-learner-changed";

export function getStoredLearner() {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "null");
  } catch {
    return null;
  }
}

// Wipe the active-learner pointer everywhere it is mirrored, and notify all
// surfaces. Used both by the manual "back to me" switch and by the automatic
// self-heal below.
export function clearStoredLearner() {
  try {
    localStorage.removeItem(KEY);
    localStorage.removeItem("academy_active_child_id");
    try {
      sessionStorage.removeItem("academy_active_child_id");
      sessionStorage.removeItem("academy_active_child_profile");
    } catch { /* ignore */ }
  } catch { /* ignore */ }
  try { window.dispatchEvent(new Event(EVT)); } catch { /* ignore */ }
}

// SELF-HEAL: drop a stored active-learner id that no longer maps to a real
// family member (a deleted, phantom, or otherwise stale profile). Without this
// a dangling pointer in localStorage silently poisons every learner-scoped
// surface ("Learning as…" switcher, Learning Forest, Upload & Learn grade,
// Quiz Battle history), which is exactly what a leftover test profile caused.
// Returns true if it cleared a stale pointer. `validIds` = self id + the
// account's real explorer ids.
export function reconcileActiveLearner(validIds) {
  let id = null;
  try {
    const stored = getStoredLearner();
    id = (stored && stored.id) || localStorage.getItem("academy_active_child_id");
  } catch { /* ignore */ }
  if (!id) return false;
  const set = new Set((validIds || []).filter(Boolean).map(String));
  if (set.size > 0 && !set.has(String(id))) {
    clearStoredLearner();
    return true;
  }
  return false;
}

// Plain (non-hook) accessor for the effective learner id, for code paths that
// aren't React components (e.g. arcade score submits). Falls back to selfId.
export function getActiveLearnerId(selfId) {
  const l = getStoredLearner();
  if (l && l.id) return l.id;
  // Fall back to the CIS-standard key that the Learn grove / curriculum use,
  // so any surface stays in sync even if only that key was set.
  try {
    const cis = localStorage.getItem("academy_active_child_id");
    if (cis) return cis;
  } catch { /* ignore */ }
  return selfId || null;
}

export function useActiveLearner() {
  const { user } = useApp();
  const [learner, setLearnerState] = useState(getStoredLearner());

  useEffect(() => {
    const sync = () => setLearnerState(getStoredLearner());
    window.addEventListener(EVT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(EVT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const setLearner = useCallback((l) => {
    const selfName = user?.first_name || user?.nickname || user?.username || "you";
    try {
      if (l && l.id) {
        localStorage.setItem(KEY, JSON.stringify(l));
        // Every learning surface must follow the switch. The CIS tools (Learn
        // page grove, curriculum chips) read localStorage.academy_active_child_id;
        // the Academy / Learning Forest suite reads sessionStorage. Write BOTH so
        // switching is consistent everywhere. Also drop the cached child profile
        // so Academy pages re-fetch for the newly active learner.
        localStorage.setItem("academy_active_child_id", l.id);
        try {
          sessionStorage.setItem("academy_active_child_id", l.id);
          sessionStorage.removeItem("academy_active_child_profile");
        } catch { /* ignore */ }
      } else {
        localStorage.removeItem(KEY);
        localStorage.removeItem("academy_active_child_id");
        try {
          sessionStorage.removeItem("academy_active_child_id");
          sessionStorage.removeItem("academy_active_child_profile");
        } catch { /* ignore */ }
      }
    } catch { /* ignore storage errors */ }
    window.dispatchEvent(new Event(EVT));
    setLearnerState(l && l.id ? l : null);
    // Reassure the parent which profile is now active. Every learning tool +
    // the arcade instantly follow this switch.
    try {
      if (l && l.id) {
        toast.success(`Now learning as ${l.name || "Explorer"}`, {
          description: "Learning tools and the arcade now use their grade levels.",
        });
      } else {
        toast.success(`Back to you (${selfName})`, {
          description: "Parent Corner and billing always stay yours.",
        });
      }
    } catch { /* toast is best-effort */ }
  }, [user]);

  const selfId = user?.profile_id || user?.id || null;
  const learnerId = learner?.id || selfId;
  const isSelf = !learner?.id || learner.id === selfId;
  const learnerName = isSelf
    ? (user?.first_name || user?.nickname || user?.username || "Me")
    : (learner?.name || "Explorer");

  return { learner, setLearner, learnerId, isSelf, learnerName, selfId, user };
}
